/**
 * Runtime environment configuration
 * Values are injected at container startup via config.js
 *
 * Only env vars that CANNOT come from the DB belong here.
 * Branding, feature flags, etc. are fetched from the settings API.
 */

declare global {
  interface Window {
    _env_?: Partial<Record<keyof ImportMetaEnv, string>>;
  }
}

type EnvKey = keyof ImportMetaEnv;

/**
 * Get runtime environment variable
 * Falls back to import.meta.env for local development
 */
export function getEnv(key: EnvKey): string {
  // Try runtime config first (for production Docker containers)
  const runtimeValue = window._env_?.[key];
  if (runtimeValue) {
    return runtimeValue;
  }

  // Fallback to build-time env for local development
  const buildTimeValue = import.meta.env[key];
  if (buildTimeValue) {
    return buildTimeValue;
  }

  const defaults: Record<EnvKey, string> = {
    VITE_API_URL: 'http://localhost:3000/api',
    VITE_CLIENT_COMPANY_NAME_INTERNAL: '',
    VITE_APP_TITLE: '',
  };

  // Only warn for keys that have defaults (non-empty)
  if (defaults[key]) {
    console.warn(`Environment variable ${key} not found, using default: ${defaults[key]}`);
  }
  return defaults[key] || '';
}
